// hooks/market/useMarketUpload.ts
//
// Upload d'une image (couverture boutique ou produit) vers le bucket
// Storage 'market'. Reproduit le pattern existant de handleProfile.ts :
//   1. Backend génère une Signed Upload URL (get-market-upload-url)
//   2. Le client upload DIRECTEMENT vers Supabase Storage via cette URL
//      (aucun transit base64 par le serveur)
//   3. Le public_url retourné est stocké sur le produit/la boutique via
//      update-product / update-shop-ui-config (ou un futur update-shop)

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

type UploadContext = 'shop-cover' | 'product-cover';

export function useMarketUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const uploadImage = useCallback(async (
    context: UploadContext,
    localUri: string,
    ids: { shop_id: string; product_id?: string }
  ): Promise<string | null> => {
    setUploading(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return null;
      }

      // Étape 1 : demander la signed URL
      const urlRes = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-market-upload-url',
          access_token: token,
          context,
          shop_id:      ids.shop_id,
          product_id:   ids.product_id,
        }),
      });

      const urlData = await urlRes.json();

      if (!urlRes.ok || !urlData.success) {
        setError(urlData.error || "Impossible d'obtenir le lien d'upload");
        return null;
      }

      // Étape 2 : upload direct vers Supabase Storage via la signed URL
      const fileBlob = await (await fetch(localUri)).blob();

      const uploadRes = await fetch(urlData.signed_url, {
        method: 'PUT',
        headers: { 'Content-Type': fileBlob.type || 'image/jpeg' },
        body: fileBlob,
      });

      if (!uploadRes.ok) {
        setError("Échec de l'upload de l'image");
        return null;
      }

      return urlData.public_url as string;
    } catch (err) {
      console.error('[useMarketUpload]', err);
      setError('network_error');
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  return { uploadImage, uploading, error };
}
